import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, HelpCircle, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button, Card } from "./ui-kit";

const STEPS = {
  storage: {
    question: "Come si conserva il prodotto?",
    options: [
      { value: "ambiente", label: "A temperatura ambiente" },
      { value: "frigorifero", label: "In frigorifero" },
      { value: "congelatore", label: "In congelatore" },
    ],
  },
  dried_or_acidified: {
    question: "A temperatura ambiente, il prodotto è...?",
    options: [
      { value: "essiccato", label: "Essiccato/secco (pane, biscotti, grissini...)" },
      { value: "conserva_acidificata", label: "Una conserva sott'olio o sottaceto acidificata" },
      { value: "nessuno", label: "Nessuno dei due" },
    ],
  },
  smoked_or_marinated_fish: {
    question: "È un prodotto a base di pesce, affumicato o marinato, pronto al consumo senza cottura?",
    options: [{ value: true, label: "Sì" }, { value: false, label: "No" }],
  },
  fresh_cheese_or_cream: {
    question: "È principalmente un formaggio fresco, oppure un dolce con crema/farcitura a base di uova o latte fresco non ulteriormente cotta?",
    options: [
      { value: "formaggio_fresco", label: "Formaggio fresco (mozzarella, ricotta, stracchino...)" },
      { value: "dolce_crema", label: "Dolce con crema/farcitura fresca (uova, latte, panna)" },
      { value: "nessuno", label: "Nessuno dei due" },
    ],
  },
  cooked: {
    question: "È stato cotto (forno, padella, bollitura...) prima di essere confezionato?",
    options: [{ value: true, label: "Sì" }, { value: false, label: "No, è crudo" }],
  },
  fresh_filled_pasta: {
    question: "È una pasta fresca ripiena (tortellini, ravioli...) da cuocere prima del consumo?",
    options: [{ value: true, label: "Sì" }, { value: false, label: "No" }],
  },
  ready_to_eat: {
    question: "È pronto al consumo così com'è, senza bisogno di ulteriore cottura?",
    options: [{ value: true, label: "Sì" }, { value: false, label: "No, richiede ancora una cottura" }],
  },
  sliced_cured_meat: {
    question: "È un salume affettato (prosciutto cotto, mortadella, wurstel...)?",
    options: [{ value: true, label: "Sì" }, { value: false, label: "No" }],
  },
  short_life_cold_gastronomy: {
    question: "È un'insalata, un panino/tramezzino farcito o un piatto di gastronomia fredda pensato per durare pochissimo (1-3 giorni)?",
    options: [{ value: true, label: "Sì" }, { value: false, label: "No" }],
  },
};

function computeNextStep(a) {
  if (a.storage === undefined) return "storage";
  if (a.storage === "congelatore") return null;
  if (a.storage === "ambiente") return a.dried_or_acidified === undefined ? "dried_or_acidified" : null;
  // frigorifero
  if (a.smoked_or_marinated_fish === undefined) return "smoked_or_marinated_fish";
  if (a.smoked_or_marinated_fish) return null;
  if (a.fresh_cheese_or_cream === undefined) return "fresh_cheese_or_cream";
  if (a.fresh_cheese_or_cream !== "nessuno") return null;
  if (a.cooked === undefined) return "cooked";
  if (!a.cooked) return a.fresh_filled_pasta === undefined ? "fresh_filled_pasta" : null;
  if (a.ready_to_eat === undefined) return "ready_to_eat";
  if (!a.ready_to_eat) return null;
  if (a.sliced_cured_meat === undefined) return "sliced_cured_meat";
  if (a.sliced_cured_meat) return null;
  if (a.short_life_cold_gastronomy === undefined) return "short_life_cold_gastronomy";
  return null;
}

export default function ShelfLifeCategoryWizard({ onApply, onClose, inline = false }) {
  const [answers, setAnswers] = useState({});
  const [answeredLog, setAnsweredLog] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const nextStepKey = computeNextStep(answers);

  useEffect(() => {
    if (nextStepKey !== null) { setResult(null); return; }
    if (Object.keys(answers).length === 0) return;
    setLoading(true);
    api.post("/app/shelf-life/category-wizard", answers)
      .then(({ data }) => setResult(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [nextStepKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = (key, value, label) => {
    setAnsweredLog([...answeredLog, { question: STEPS[key].question, label }]);
    setAnswers({ ...answers, [key]: value });
  };

  const restart = () => { setAnswers({}); setAnsweredLog([]); setResult(null); };

  const step = nextStepKey ? STEPS[nextStepKey] : null;

  const body = (
    <>
      {!inline && <h2 className="pr-10 flex items-center gap-2 font-heading text-lg font-bold text-navy"><HelpCircle className="h-5 w-5 text-gold" /> Aiutami a scegliere la categoria</h2>}
      {!inline && <p className="mt-1 text-sm text-slate-500">Rispondi in base alle caratteristiche reali del prodotto, non al nome: è più affidabile.</p>}

      {answeredLog.length > 0 && (
        <div className={inline ? "space-y-1.5" : "mt-4 space-y-1.5"}>
          {answeredLog.map((a, i) => (
            <div key={i} className="rounded-lg bg-white px-3 py-1.5 text-xs text-slate-500 ring-1 ring-indigo-100"><span className="text-slate-400">{a.question}</span> → <span className="font-medium text-navy">{a.label}</span></div>
          ))}
        </div>
      )}

      {step && (
        <div className={answeredLog.length > 0 ? "mt-3" : "mt-1"}>
          <div className="mb-3 text-sm font-semibold text-navy">{step.question}</div>
          <div className={inline ? "grid gap-2 sm:grid-cols-2" : "space-y-2"}>
            {step.options.map((opt) => (
              <button key={String(opt.value)} type="button" onClick={() => answer(nextStepKey, opt.value, opt.label)}
                className={`rounded-lg border-2 bg-white px-4 py-2.5 text-left text-sm text-navy transition-colors hover:border-indigo-400 hover:bg-indigo-50 ${inline ? "border-indigo-200 animate-pulse ring-2 ring-indigo-200 ring-offset-1" : "border-hairline block w-full"}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!step && loading && <div className="mt-3 text-sm text-slate-400">Calcolo in corso...</div>}

      {!step && !loading && result && (
        <div className="mt-3">
          {result.category_id ? (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div>
                  <div className="font-semibold text-emerald-900">Categoria individuata: {result.name}</div>
                  <div className="mt-2"><Button variant="success" onClick={() => onApply(result.category_id, result.name)}>Applica questa categoria</Button></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900">{result.reason}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {answeredLog.length > 0 && (
        <button type="button" onClick={restart} className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-navy"><RotateCcw className="h-3.5 w-3.5" /> Ricomincia</button>
      )}
    </>
  );

  if (inline) return body;

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center bg-navy/60 p-4 backdrop-blur-sm overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="relative mt-6 w-full max-w-lg p-6 sm:p-8">
        <button type="button" onClick={onClose} className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Chiudi"><X className="h-5 w-5" /></button>
        {body}
      </Card>
    </div>
  );
}
